import {
  ModelFamily,
  ModelSurface,
  ModelTopology,
  ReferenceTransportStrategy,
} from './schema';
import {
  MESHY_ENDPOINTS,
  RODIN_ENDPOINTS,
  TRIPO_3D_ENDPOINTS,
} from './falEndpoints.js';

const DEFAULT_CHECKED_AT = '2026-04-25';

const noReferences = {
  acceptedKinds: [],
  acceptedMediaTypes: [],
  roles: [],
  chips: [],
  limits: { total: 0 },
  transport: { strategy: ReferenceTransportStrategy.NONE },
  unsupportedBehavior: 'text-only',
};

const sourceImageReference = {
  acceptedKinds: ['source', 'character', 'prop'],
  acceptedMediaTypes: ['image'],
  roles: ['source'],
  chips: ['source'],
  limits: { total: 1, image: 1 },
  transport: {
    strategy: ReferenceTransportStrategy.SOURCE_SLOT,
    fields: { source: 'image' },
  },
  unsupportedBehavior: 'block',
};

const multiSourceImageReference = {
  acceptedKinds: ['source', 'character', 'prop'],
  acceptedMediaTypes: ['image'],
  roles: ['source'],
  chips: ['source'],
  limits: { total: 4, image: 4 },
  transport: {
    strategy: ReferenceTransportStrategy.FLAT,
    fields: { images: 'imageUrls' },
  },
  unsupportedBehavior: 'block',
};

const sourceModelReference = {
  acceptedKinds: ['source-model'],
  acceptedMediaTypes: ['3d'],
  roles: ['source-model'],
  chips: ['source-model'],
  limits: { total: 1, model: 1 },
  transport: {
    strategy: ReferenceTransportStrategy.SOURCE_SLOT,
    fields: { source: 'modelUrl' },
  },
  unsupportedBehavior: 'block',
};

const sourceModelWithTextureReference = {
  acceptedKinds: ['source-model', 'texture-reference'],
  acceptedMediaTypes: ['3d', 'image'],
  roles: ['source-model', 'texture-reference'],
  chips: ['source-model', 'texture-reference'],
  limits: { total: 2, model: 1, image: 1 },
  transport: {
    strategy: ReferenceTransportStrategy.NATIVE_SLOTS,
    fields: {
      source: 'modelUrl',
      image: 'textureImage',
    },
  },
  unsupportedBehavior: 'block',
};

function verification(endpoint) {
  return {
    source: 'curated-existing-config',
    provider: 'fal',
    endpoint,
    checkedAt: DEFAULT_CHECKED_AT,
    unmappedProviderParams: [],
  };
}

function threeDFeatures({
  promptInImageMode = false,
  characterImageInput = false,
  maxSourceImages = 1,
  promptInput = false,
  referenceImageInput = false,
  remesh = false,
  meshTopology = false,
  targetPolycount = false,
  symmetryMode = false,
  texture = false,
  pbr = false,
  poseMode = false,
  rigging = false,
  animation = false,
} = {}) {
  return {
    promptInImageMode: { enabled: promptInImageMode },
    characterImageInput: { enabled: characterImageInput },
    multiImageInput: { enabled: maxSourceImages > 1, max: maxSourceImages },
    promptInput: { enabled: promptInput },
    referenceImageInput: { enabled: referenceImageInput },
    remesh: { enabled: remesh },
    meshTopology: meshTopology
      ? { enabled: true, options: ['triangle', 'quad'], default: 'triangle' }
      : { enabled: false },
    targetPolycount: targetPolycount
      ? { enabled: true, min: 1000, max: 300000, default: 30000 }
      : { enabled: false },
    symmetryMode: symmetryMode
      ? { enabled: true, options: ['auto', 'off', 'on'], default: 'auto' }
      : { enabled: false },
    texture: texture
      ? { enabled: true, default: true }
      : { enabled: false },
    pbr: pbr
      ? { enabled: true, default: false, requires: 'texture' }
      : { enabled: false },
    poseMode: poseMode
      ? { enabled: true, options: ['', 'a-pose', 't-pose'], default: '' }
      : { enabled: false },
    rigging: rigging
      ? { enabled: true, default: false }
      : { enabled: false },
    animation: animation
      ? { enabled: true, default: false, requires: 'rigging' }
      : { enabled: false },
    outputFormats: { options: ['glb'] },
  };
}

function route({
  endpoint,
  adapterId,
  serviceKey,
  serviceMethod,
  features = {},
  references = noReferences,
}) {
  return {
    endpoint,
    features,
    references,
    execution: {
      type: 'fal-service-method',
      adapterId,
      serviceKey,
      serviceMethod,
      endpoint,
    },
  };
}

function timing(baseSeconds, options = {}) {
  return {
    source: 'curated-existing-config',
    baseSeconds,
    ...options,
  };
}

export const THREE_D_MODEL_DEFINITIONS = [
  {
    id: 'trellis',
    label: 'Trellis 2',
    family: ModelFamily.THREE_D,
    iconKey: 'microsoft',
    pricing: { unit: 'generation', usd: 0.3, asOf: '2026-04-25' },
    timing: timing(45),
    surfaces: [ModelSurface.THREE_D_GENERATE],
    topologies: [ModelTopology.IMAGE_TO_THREE_D],
    features: threeDFeatures({ characterImageInput: true }),
    references: sourceImageReference,
    routes: {
      [ModelTopology.IMAGE_TO_THREE_D]: route({
        endpoint: 'fal-ai/trellis-2',
        adapterId: '3d.image.source',
        serviceKey: 'trellis',
        serviceMethod: 'generateTrellis',
      }),
    },
    prompt: null,
    agent: { exposeAs: ['3d-generator'] },
    verification: verification('fal-ai/trellis-2'),
  },
  {
    id: 'tripo3d',
    label: 'Tripo3D v2.5',
    family: ModelFamily.THREE_D,
    iconKey: 'tripo',
    pricing: { unit: 'generation', usd: 0.3, asOf: '2026-04-25' },
    timing: timing(12),
    surfaces: [ModelSurface.THREE_D_GENERATE],
    topologies: [ModelTopology.IMAGE_TO_THREE_D],
    features: threeDFeatures({ promptInImageMode: true, characterImageInput: true }),
    references: sourceImageReference,
    routes: {
      [ModelTopology.IMAGE_TO_THREE_D]: route({
        endpoint: TRIPO_3D_ENDPOINTS.imageToThreeD,
        adapterId: '3d.image.prompt-optional',
        serviceKey: 'tripo3d',
        serviceMethod: 'generateTripoImageTo3D',
      }),
    },
    prompt: null,
    agent: { exposeAs: ['3d-generator'] },
    verification: verification(TRIPO_3D_ENDPOINTS.imageToThreeD),
  },
  {
    id: 'hunyuan3d',
    label: 'Hunyuan3D Pro',
    family: ModelFamily.THREE_D,
    iconKey: 'hunyuan',
    pricing: { unit: 'generation', usd: 0.38, asOf: '2026-04-25' },
    timing: timing(70),
    surfaces: [ModelSurface.THREE_D_GENERATE],
    topologies: [ModelTopology.TEXT_TO_THREE_D, ModelTopology.IMAGE_TO_THREE_D],
    features: threeDFeatures({ promptInImageMode: true, characterImageInput: true, promptInput: true }),
    references: sourceImageReference,
    routes: {
      [ModelTopology.TEXT_TO_THREE_D]: route({
        endpoint: 'fal-ai/hunyuan-3d/v3.1/pro/text-to-3d',
        adapterId: '3d.text.prompt',
        serviceKey: 'hunyuan3d',
        serviceMethod: 'generateHunyuanTextTo3D',
        references: noReferences,
      }),
      [ModelTopology.IMAGE_TO_THREE_D]: route({
        endpoint: 'fal-ai/hunyuan-3d/v3.1/pro/image-to-3d',
        adapterId: '3d.image.prompt-optional',
        serviceKey: 'hunyuan3d',
        serviceMethod: 'generateHunyuanImageTo3D',
      }),
    },
    prompt: { enhancerId: '3d-hunyuan' },
    agent: { exposeAs: ['3d-generator'] },
    verification: verification('fal-ai/hunyuan-3d/v3.1/pro/text-to-3d'),
  },
  {
    id: 'rodin',
    label: 'Hyper3D Rodin v2',
    family: ModelFamily.THREE_D,
    iconKey: 'rodin',
    pricing: { unit: 'generation', usd: 0.4, asOf: '2026-04-25' },
    timing: timing(85),
    surfaces: [ModelSurface.THREE_D_GENERATE],
    topologies: [ModelTopology.IMAGE_TO_THREE_D],
    features: threeDFeatures({ promptInImageMode: true, characterImageInput: true }),
    references: sourceImageReference,
    routes: {
      [ModelTopology.IMAGE_TO_THREE_D]: route({
        endpoint: RODIN_ENDPOINTS.imageToThreeD,
        adapterId: '3d.image.prompt-optional',
        serviceKey: 'hyper3dRodin',
        serviceMethod: 'generateRodinImageTo3D',
      }),
    },
    prompt: { enhancerId: '3d-rodin' },
    agent: { exposeAs: ['3d-generator'] },
    verification: verification(RODIN_ENDPOINTS.imageToThreeD),
  },
  {
    id: 'meshy',
    label: 'Meshy 7',
    family: ModelFamily.THREE_D,
    iconKey: 'meshy',
    pricing: { unit: 'generation', usd: 0.8, asOf: '2026-04-25' },
    timing: timing(480),
    surfaces: [ModelSurface.THREE_D_GENERATE],
    topologies: [ModelTopology.TEXT_TO_THREE_D, ModelTopology.IMAGE_TO_THREE_D],
    features: threeDFeatures({ promptInImageMode: true, characterImageInput: true, promptInput: true }),
    references: sourceImageReference,
    routes: {
      [ModelTopology.TEXT_TO_THREE_D]: route({
        endpoint: MESHY_ENDPOINTS.textToThreeD,
        adapterId: '3d.text.prompt',
        serviceKey: 'meshy',
        serviceMethod: 'generateMeshyTextTo3D',
        references: noReferences,
      }),
      [ModelTopology.IMAGE_TO_THREE_D]: route({
        endpoint: MESHY_ENDPOINTS.imageToThreeD,
        adapterId: '3d.image.prompt-optional',
        serviceKey: 'meshy',
        serviceMethod: 'generateMeshyImageTo3D',
      }),
    },
    prompt: { enhancerId: '3d-rodin' },
    agent: { exposeAs: ['3d-generator'] },
    verification: verification(MESHY_ENDPOINTS.imageToThreeD),
  },
  {
    id: 'meshy-multi-image',
    label: 'Meshy 7 Multi-Image',
    family: ModelFamily.THREE_D,
    iconKey: 'meshy',
    pricing: { unit: 'generation', usd: 0.8, asOf: '2026-04-25' },
    timing: timing(480),
    surfaces: [ModelSurface.THREE_D_GENERATE],
    topologies: [ModelTopology.IMAGE_TO_THREE_D],
    features: threeDFeatures({
      promptInImageMode: false,
      characterImageInput: true,
      maxSourceImages: 4,
      meshTopology: true,
      targetPolycount: true,
      symmetryMode: true,
      texture: true,
      pbr: true,
      poseMode: true,
      rigging: true,
      animation: true,
    }),
    references: multiSourceImageReference,
    routes: {
      [ModelTopology.IMAGE_TO_THREE_D]: route({
        endpoint: MESHY_ENDPOINTS.multiImageToThreeD,
        adapterId: '3d.image.multi-source',
        serviceKey: 'meshy',
        serviceMethod: 'generateMeshyMultiImageTo3D',
        references: multiSourceImageReference,
      }),
    },
    prompt: null,
    agent: { exposeAs: ['3d-generator'] },
    description: 'Generate a 3D model from 1-4 object images captured from different angles.',
    verification: {
      ...verification(MESHY_ENDPOINTS.multiImageToThreeD),
      source: 'fal-api-docs',
      unmappedProviderParams: ['texture_prompt', 'texture_image_url', 'rigging_height_meters', 'animation_action_id'],
    },
  },
  {
    id: 'trellis-retexture',
    label: 'Trellis Retexture',
    family: ModelFamily.THREE_D,
    iconKey: 'microsoft',
    pricing: { unit: 'generation', usd: 0.35, asOf: '2026-04-25' },
    timing: timing(60),
    surfaces: [ModelSurface.THREE_D_EDIT],
    topologies: [ModelTopology.THREE_D_TO_THREE_D],
    features: threeDFeatures({ referenceImageInput: true }),
    references: sourceModelWithTextureReference,
    routes: {
      [ModelTopology.THREE_D_TO_THREE_D]: route({
        endpoint: 'fal-ai/trellis-2/retexture',
        adapterId: '3d.retexture.reference-image',
        serviceKey: 'retexture',
        serviceMethod: 'retextureModel',
      }),
    },
    prompt: null,
    // No agent tool reaches this capability, so an `exposeAs` here rendered a
    // Settings > Models agent-default row the agent could never honour. Manual
    // use is unaffected: `exposeAs` gates agent surfaces only. Re-add the role in
    // the same change that adds the tool.
    agent: { exposeAs: [] },
    verification: verification('fal-ai/trellis-2/retexture'),
  },
  {
    id: 'meshy-retexture',
    label: 'Meshy Retexture',
    family: ModelFamily.THREE_D,
    iconKey: 'meshy',
    pricing: { unit: 'generation', usd: 0.5, asOf: '2026-04-25' },
    timing: timing(240),
    surfaces: [ModelSurface.THREE_D_EDIT],
    topologies: [ModelTopology.THREE_D_TO_THREE_D],
    features: threeDFeatures({ promptInput: true, referenceImageInput: true }),
    references: sourceModelWithTextureReference,
    routes: {
      [ModelTopology.THREE_D_TO_THREE_D]: route({
        endpoint: 'fal-ai/meshy/v5/retexture',
        adapterId: '3d.retexture.prompt',
        serviceKey: 'retexture',
        serviceMethod: 'retextureModel',
      }),
    },
    prompt: { enhancerId: '3d-retexture-meshy' },
    // No agent tool reaches this capability, so an `exposeAs` here rendered a
    // Settings > Models agent-default row the agent could never honour. Manual
    // use is unaffected: `exposeAs` gates agent surfaces only. Re-add the role in
    // the same change that adds the tool.
    agent: { exposeAs: [] },
    verification: verification('fal-ai/meshy/v5/retexture'),
  },
  {
    id: 'meshy-remesh',
    label: 'Meshy Remesh',
    family: ModelFamily.THREE_D,
    iconKey: 'meshy',
    pricing: { unit: 'generation', usd: 0.3, asOf: '2026-04-25' },
    timing: timing(150),
    surfaces: [ModelSurface.THREE_D_EDIT],
    topologies: [ModelTopology.THREE_D_TO_THREE_D],
    features: threeDFeatures({ remesh: true }),
    references: sourceModelReference,
    routes: {
      [ModelTopology.THREE_D_TO_THREE_D]: route({
        endpoint: 'fal-ai/meshy/v5/remesh',
        adapterId: '3d.remesh.source-model',
        serviceKey: 'remesh',
        serviceMethod: 'remeshModel',
      }),
    },
    prompt: null,
    // No agent tool reaches this capability, so an `exposeAs` here rendered a
    // Settings > Models agent-default row the agent could never honour. Manual
    // use is unaffected: `exposeAs` gates agent surfaces only. Re-add the role in
    // the same change that adds the tool.
    agent: { exposeAs: [] },
    verification: verification('fal-ai/meshy/v5/remesh'),
  },
];
